import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { FinancialConfig } from './FinancialConfig.js';
import { formatInputValue, getNumericInputProps } from './utils/inputHelpers.js';

const SMSFWorkTestTool = ({ smsfState, setSmsfState }) => {
    const [activeTab, setActiveTab] = useState('tracker');
    
    const [workData, setWorkData] = useState({
        age: 68,
        financialYear: new Date().getFullYear(),
        workPeriods: [],
        plannedContribution: 25000,
        contributionType: 'concessional'
    });

    const [newPeriod, setNewPeriod] = useState({
        startDate: '',
        endDate: '',
        hoursWorked: 0,
        employerName: '',
        description: ''
    });

    const WORK_TEST = FinancialConfig.WORK_TEST;

    const addWorkPeriod = () => {
        if (!newPeriod.startDate || !newPeriod.endDate || newPeriod.hoursWorked === 0) {
            alert('Please fill in all fields');
            return;
        }

        const start = new Date(newPeriod.startDate);
        const end = new Date(newPeriod.endDate);
        const days = Math.floor((end - start) / (1000 * 60 * 60 * 24)) + 1;

        setWorkData({
            ...workData,
            workPeriods: [...workData.workPeriods, {
                ...newPeriod,
                id: Date.now(),
                days,
                meetsRequirement: days <= 30 && newPeriod.hoursWorked >= WORK_TEST.MINIMUM_HOURS
            }]
        });

        setNewPeriod({
            startDate: '',
            endDate: '',
            hoursWorked: 0,
            employerName: '',
            description: ''
        });
    };

    const deleteWorkPeriod = (id) => {
        setWorkData({
            ...workData,
            workPeriods: workData.workPeriods.filter(p => p.id !== id)
        });
    };

    const checkEligibility = () => {
        // Check if any period meets the requirement
        const qualifyingPeriods = workData.workPeriods.filter(p => p.meetsRequirement);
        const passed = qualifyingPeriods.length > 0;

        // Age-based eligibility
        const needsWorkTest = workData.age >= WORK_TEST.AGE_THRESHOLD && workData.age < 75;
        const canContribute = workData.age < 75;

        return {
            needsWorkTest,
            canContribute,
            passed,
            qualifyingPeriods,
            totalHours: workData.workPeriods.reduce((sum, p) => sum + p.hoursWorked, 0)
        };
    };

    const eligibility = checkEligibility();

    const renderTracker = () => (
        <div className="space-y-6">
            {/* Eligibility Summary */}
            <div className={`border-l-4 p-6 rounded-lg ${
                eligibility.needsWorkTest ? 'bg-yellow-50 border-yellow-500' : 'bg-green-50 border-green-500'
            }`}>
                <h3 className="font-semibold mb-2">
                    {eligibility.needsWorkTest ? '⚠️ Work Test Required' : '✅ No Work Test Required'}
                </h3>
                <p className="text-sm mb-2">
                    Age: {workData.age} years old
                </p>
                {eligibility.needsWorkTest && (
                    <>
                        <p className="text-sm mb-2">
                            To make contributions, you must work at least <strong>{WORK_TEST.MINIMUM_HOURS} hours in a consecutive 30-day period</strong> during the financial year.
                        </p>
                        <div className={`mt-3 px-4 py-2 rounded ${
                            eligibility.passed ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                        }`}>
                            <strong>Status:</strong> {eligibility.passed ? '✓ Work test MET' : '✗ Work test NOT met'}
                        </div>
                    </>
                )}
            </div>

            {/* Personal Details */}
            <div className="bg-white p-6 rounded-lg shadow">
                <h3 className="font-semibold text-lg mb-4">Details</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Age</label>
                        <input
                            {...getNumericInputProps()}
                            value={workData.age}
                            onChange={(e) => setWorkData({ ...workData, age: Number(e.target.value) })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Financial Year</label>
                        <input
                            {...getNumericInputProps()}
                            value={workData.financialYear}
                            onChange={(e) => setWorkData({ ...workData, financialYear: Number(e.target.value) })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Planned Contribution</label>
                        <input
                            {...getNumericInputProps()}
                            value={workData.plannedContribution}
                            onChange={(e) => setWorkData({ ...workData, plannedContribution: Number(e.target.value) })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md"
                        />
                    </div>
                </div>
            </div>

            {/* Add Work Period */}
            <div className="bg-white p-6 rounded-lg shadow">
                <h3 className="font-semibold text-lg mb-4">Record Work Period</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Hours Worked</label>
                        <input
                            {...getNumericInputProps()}
                            value={newPeriod.hoursWorked}
                            onChange={(e) => setNewPeriod({ ...newPeriod, hoursWorked: Number(e.target.value) })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md"
                            placeholder="40"
                        />
                        <p className="text-xs text-gray-500 mt-1">Minimum: {WORK_TEST.MINIMUM_HOURS} hours</p>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Days Worked</label>
                        <input
                            {...getNumericInputProps()}
                            value={newPeriod.daysWorked || ''}
                            onChange={(e) => {
                                const days = Number(e.target.value) || 0;
                                setNewPeriod({ ...newPeriod, daysWorked: days });
                                // Auto-set dates if days entered
                                if (days > 0 && days <= 30) {
                                    const today = new Date();
                                    const endDate = today.toISOString().split('T')[0];
                                    const startDate = new Date(today.getTime() - (days - 1) * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
                                    setNewPeriod(prev => ({ ...prev, startDate, endDate }));
                                }
                            }}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md"
                            placeholder="30"
                        />
                        <p className="text-xs text-gray-500 mt-1">Maximum: 30 consecutive days</p>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Work Period</label>
                        <select
                            value={newPeriod.workPeriod || 'last30'}
                            onChange={(e) => {
                                setNewPeriod({ ...newPeriod, workPeriod: e.target.value });
                                // Auto-set dates based on selection
                                const today = new Date();
                                if (e.target.value === 'last30') {
                                    const endDate = today.toISOString().split('T')[0];
                                    const startDate = new Date(today.getTime() - 29 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
                                    setNewPeriod(prev => ({ ...prev, startDate, endDate, daysWorked: 30 }));
                                }
                            }}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md"
                        >
                            <option value="last30">Last 30 days</option>
                            <option value="custom">Custom period</option>
                        </select>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Employment Type</label>
                        <select
                            value={newPeriod.employmentType || 'employed'}
                            onChange={(e) => setNewPeriod({ ...newPeriod, employmentType: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md"
                        >
                            <option value="employed">Employed</option>
                            <option value="self-employed">Self-Employed</option>
                            <option value="casual">Casual</option>
                            <option value="contract">Contract</option>
                        </select>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
                        <input
                            type="date"
                            value={newPeriod.startDate}
                            onChange={(e) => setNewPeriod({ ...newPeriod, startDate: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
                        <input
                            type="date"
                            value={newPeriod.endDate}
                            onChange={(e) => setNewPeriod({ ...newPeriod, endDate: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md"
                        />
                    </div>
                </div>
                <button
                    type="button"
                    onClick={addWorkPeriod}
                    className="mt-4 bg-blue-600 text-white py-2 px-6 rounded-lg hover:bg-blue-700"
                >
                    Add Period
                </button>
            </div>

            {/* Work Periods List */}
            {workData.workPeriods.length > 0 && (
                <div className="bg-white p-6 rounded-lg shadow">
                    <h3 className="font-semibold text-lg mb-4">Recorded Work Periods</h3>
                    <div className="space-y-3">
                        {workData.workPeriods.map(period => (
                            <div key={period.id} className={`border rounded-lg p-4 ${
                                period.meetsRequirement ? 'border-green-300 bg-green-50' : 'border-gray-300'
                            }`}>
                                <div className="flex justify-between items-start mb-2">
                                    <div>
                                        <div className="font-medium">
                                            {new Date(period.startDate).toLocaleDateString()} - {new Date(period.endDate).toLocaleDateString()}
                                        </div>
                                        <div className="text-sm text-gray-600">{period.employerName}</div>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => deleteWorkPeriod(period.id)}
                                        className="text-red-600 hover:text-red-700 text-sm"
                                    >
                                        Delete
                                    </button>
                                </div>
                                <div className="grid grid-cols-3 gap-4 text-sm mt-3">
                                    <div>
                                        <span className="text-gray-600">Days:</span>
                                        <span className="ml-2 font-semibold">{period.days}</span>
                                    </div>
                                    <div>
                                        <span className="text-gray-600">Hours:</span>
                                        <span className="ml-2 font-semibold">{period.hoursWorked}</span>
                                    </div>
                                    <div>
                                        <span className={period.meetsRequirement ? 'text-green-600 font-semibold' : 'text-gray-600'}>
                                            {period.meetsRequirement ? '✓ Qualifies' : '○ Insufficient'}
                                        </span>
                                    </div>
                                </div>
                                {period.description && (
                                    <p className="text-sm text-gray-600 mt-2">{period.description}</p>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Summary */}
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-6 rounded-lg shadow">
                <h4 className="font-semibold mb-3">Summary</h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div>
                        <div className="text-gray-600">Total Hours</div>
                        <div className="text-2xl font-bold text-blue-600">{eligibility.totalHours}</div>
                    </div>
                    <div>
                        <div className="text-gray-600">Qualifying Periods</div>
                        <div className="text-2xl font-bold text-green-600">{eligibility.qualifyingPeriods.length}</div>
                    </div>
                    <div>
                        <div className="text-gray-600">Work Test</div>
                        <div className={`text-lg font-bold ${eligibility.passed ? 'text-green-600' : 'text-red-600'}`}>
                            {eligibility.passed ? '✓ MET' : '✗ NOT MET'}
                        </div>
                    </div>
                    <div>
                        <div className="text-gray-600">Can Contribute</div>
                        <div className={`text-lg font-bold ${eligibility.canContribute && eligibility.passed ? 'text-green-600' : 'text-red-600'}`}>
                            {eligibility.canContribute && (eligibility.passed || !eligibility.needsWorkTest) ? 'YES' : 'NO'}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );

    const renderEducation = () => (
        <div className="space-y-6">
            <div className="bg-white p-6 rounded-lg shadow">
                <h3 className="font-semibold text-lg mb-4">📚 Work Test Requirements</h3>
                
                <div className="space-y-4">
                    <div>
                        <h4 className="font-semibold text-gray-900 mb-2">Who Needs to Meet the Work Test?</h4>
                        <p className="text-gray-700 mb-2">
                            If you're aged {WORK_TEST.AGE_THRESHOLD}-74 and want to make voluntary contributions to super, 
                            you must meet the work test: <strong>at least {WORK_TEST.MINIMUM_HOURS} hours of gainful employment 
                            in a consecutive 30-day period</strong> during the financial year.
                        </p>
                    </div>

                    <div className="bg-blue-50 p-4 rounded-lg">
                        <h4 className="font-semibold text-blue-900 mb-2">What Counts as "Gainful Employment"?</h4>
                        <ul className="list-disc list-inside text-sm text-blue-800 space-y-1">
                            <li>Paid employment (full-time, part-time, casual)</li>
                            <li>Self-employment or running a business</li>
                            <li>Work must be for financial gain (not volunteer work)</li>
                            <li>Can be multiple jobs adding up to 40 hours</li>
                        </ul>
                    </div>

                    <div>
                        <h4 className="font-semibold text-gray-900 mb-2">Exemptions</h4>
                        <ul className="list-disc list-inside text-sm text-gray-700 space-y-1">
                            <li>Under age {WORK_TEST.AGE_THRESHOLD}: No work test required</li>
                            <li>Downsizer contributions: Exempt from work test</li>
                            <li>Carry-forward rule: Still requires work test for ages {WORK_TEST.AGE_THRESHOLD}-74</li>
                        </ul>
                    </div>

                    <div className="bg-yellow-50 p-4 rounded-lg">
                        <h4 className="font-semibold text-yellow-900 mb-2">⚠️ Recordkeeping</h4>
                        <p className="text-sm text-yellow-800 mb-2">
                            Evidence that may be kept for meeting the work test includes:
                        </p>
                        <ul className="list-disc list-inside text-sm text-yellow-700 space-y-1">
                            <li>Pay slips showing dates and hours</li>
                            <li>Employment contracts or letters</li>
                            <li>Business records (if self-employed)</li>
                            <li>Diary or timesheet of work performed</li>
                        </ul>
                    </div>

                    <div className="bg-green-50 p-4 rounded-lg">
                        <h4 className="font-semibold text-green-900 mb-2">Example</h4>
                        <p className="text-sm text-green-800">
                            John (age 70) works 20 hours/week for 2 weeks (July 1-14) and then 10 hours/week for 2 more weeks (July 15-28).
                            <br/><strong>Total: 60 hours over 28 consecutive days ✓</strong>
                            <br/>John meets the work test and can contribute to super.
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );

    return (
        <div className="max-w-7xl mx-auto p-6">
            <div className="mb-6">
                <h2 className="text-3xl font-bold text-gray-900 mb-2">Work Test Verification Tool</h2>
                <p className="text-gray-600">
                    Track your work hours to verify contribution eligibility (ages {WORK_TEST.AGE_THRESHOLD}-74)
                </p>
            </div>

            <div className="border-b border-gray-200 mb-6">
                <nav className="flex space-x-8">
                    {[
                        { id: 'tracker', label: 'Work Tracker', icon: '⏱️' },
                        { id: 'education', label: 'Requirements', icon: '📚' }
                    ].map(tab => (
                        <button
                            key={tab.id}
                            onClick={() => setActiveTab(tab.id)}
                            className={`py-4 px-1 border-b-2 font-medium text-sm ${
                                activeTab === tab.id
                                    ? 'border-blue-500 text-blue-600'
                                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                            }`}
                        >
                            <span className="mr-2">{tab.icon}</span>
                            {tab.label}
                        </button>
                    ))}
                </nav>
            </div>

            <div>
                {activeTab === 'tracker' && renderTracker()}
                {activeTab === 'education' && renderEducation()}
            </div>
        </div>
    );
};

SMSFWorkTestTool.propTypes = {
    smsfState: PropTypes.object.isRequired,
    setSmsfState: PropTypes.func.isRequired
};

export default SMSFWorkTestTool;

